<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useDatePickerLocalization } from '@/composables/useDatePickerLocalization'

import { Checkbox as KCheckBox, type CheckboxChangeEvent  } from '@progress/kendo-vue-inputs'
import { DatePicker, type DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { LocalizationProvider, IntlProvider } from '@progress/kendo-vue-intl'
import { getKendoLanguage, getKendoLocale } from '@/utils/kendoHelper'

import { ref, onMounted, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart } from '@/types/CartTypes'

import { CreateConsultantLeadCart, DeleteConsultantLeadCart, CreatePartyLeadCart, DeletePartyLeadCart, UpdateCartActive } from '@/services/CartService'
import { formatDateTimeForJSONRequest, formatDate } from "@/api/Common.ts";

const logger = useLogger()

const { dateFormat, formatPlaceholder } = useDatePickerLocalization(locale)

const interestedInBecomeConsultant = ref<boolean | null>(null)
const interestedInHostParty = ref<boolean | null>(null)
const hostPartyDate = ref<Date | null>(null)
const isCancelled = ref<boolean>(false)
const isPartyDateSaved = ref<boolean>(false)

const emit = defineEmits(["update-cart"]);

const props = defineProps<{
  cart: IAoeCart | null
}>()

function initializePartyState() {
  logger.debug('initializePartyState - checking cart data:', props.cart?.isPartyLead, props.cart?.leadPartyDate)

  // Initialize state based on existing cart data
  if (props.cart?.isPartyLead && props.cart?.leadPartyDate) {
    logger.debug('User already has party lead saved, initializing to saved state')
    interestedInHostParty.value = true
    isCancelled.value = false
    isPartyDateSaved.value = true

    // Parse the leadPartyDate and set it to hostPartyDate
    try {
      const leadDate = new Date(
        props.cart.leadPartyDate.split('-')[0],
        props.cart.leadPartyDate.split('-')[1] - 1,
        props.cart.leadPartyDate.split('-')[2].split('T')[0],
        props.cart.leadPartyDate.split('-')[2].split('T')[1].split(':')[0],
        props.cart.leadPartyDate.split('-')[2].split('T')[1].split(':')[1],
        props.cart.leadPartyDate.split('-')[2].split('T')[1].split(':')[2].split('.')[0],
        0
      )
      hostPartyDate.value = leadDate
      logger.debug('Successfully parsed and set party date:', leadDate)
    } catch (error) {
      logger.error('Error parsing leadPartyDate:', error)
    }
  } else {
    logger.debug('No existing party lead found or missing date')
    interestedInHostParty.value = false
    isCancelled.value = false
    isPartyDateSaved.value = false
    hostPartyDate.value = null
  }
}

// Watch for cart changes to initialize state when data becomes available
watch(
  () => props.cart,
  (newCart) => {
    if (newCart) {
      logger.debug('Cart data received, initializing party state')
      initializePartyState()
    }
  },
  { immediate: true }
)

onMounted(async () => {
  logger.debug('onMounted')
  // Try to initialize immediately in case cart is already available
  initializePartyState()
})

function changeConsultantLeadCheckboxHandler(event: CheckboxChangeEvent)
{
  logger.debug('changeConsultantLeadCheckboxHandler', event)
  interestedInBecomeConsultant.value = event.value
  if (event.value)
  {
    CreateConsultantLeadCart({
      aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : ''
    }).then(() => {
      logger.debug('CreateConsultantLeadCart success')
    })
  }
  else
  {
    DeleteConsultantLeadCart({
      aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : ''
    }).then(() => {
      logger.debug('DeleteConsultantLeadCart success')
    })
  }
}

function hostPartyDateClickedHandler(event: MouseEvent)
{
  logger.debug('hostPartyDateClickedHandler', event, hostPartyDate.value)
  if (hostPartyDate.value == null)
  {
    logger.debug('hostPartyDateClickedHandler: hostPartyDate is null')
    return
  }

  CreatePartyLeadCart({
      aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : '',
      partyDate: formatDateTimeForJSONRequest(hostPartyDate.value)
    }).then(() => {
      logger.debug('CreatePartyLeadCart success')
      isPartyDateSaved.value = true
      emit("update-cart", event)
    })
}

function hostPartyDateCancelClickedHandler(event: MouseEvent)
{
  logger.debug('hostPartyDateCancelClickedHandler', event, hostPartyDate.value)
  interestedInHostParty.value = false
  hostPartyDate.value = null
  isCancelled.value = true
  isPartyDateSaved.value = false

  DeletePartyLeadCart({
      aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : '',
      partyDate: null
    }).then(() => {
      logger.debug('DeletePartyLeadCart success')
      emit("update-cart", event)
    })
}

function hostPartyCheckboxClickedHandler(event: CheckboxChangeEvent)
{
  logger.debug('hostPartyCheckboxClickedHandler', event, interestedInHostParty.value, isCancelled.value)
  interestedInHostParty.value = event.value
  isCancelled.value = !event.value

  // Clear saved state and date when unchecking
  if (!event.value) {
    isPartyDateSaved.value = false
    hostPartyDate.value = null
  }
}

function leadPartyDateChangedHandler(event: DatePickerChangeEvent)
{
  logger.debug('leadPartyDateChangedHandler', event)
  hostPartyDate.value = event.value
}

function changePartyDateClickedHandler(event: MouseEvent)
{
  logger.debug('changePartyDateClickedHandler', event)
  isPartyDateSaved.value = false
  // Keep the existing date value so user can modify it
}

function reactivateCartHandler(event: CheckboxChangeEvent) {
  logger.debug('reactivateCartHandler', event)
  if (event.value && props.cart?.aoeCartIdToken) {
    UpdateCartActive({
      aoeCartIdToken: props.cart.aoeCartIdToken,
      active: true
    }).then(() => {
      emit("update-cart", event)
    })
  }
}
</script>

<template>
  <div v-if="props.cart != null && props.cart.aoeCartIdToken != ''" class="aoe-guest-header-control">
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div guest-header-section-header">
        {{ props.cart.isHostCart ? t('partyOrderEntry.HostOrderHeaderSectionHeader') : t('partyOrderEntry.GuestOrderHeaderSectionHeader') }}
      </div>
    </div>
    <div class="row mt-3 guest-name-row">
      <div class="col-6 guest-name-div">
        {{ t('partyOrderEntry.GuestName') }}: {{ props.cart?.beeNumberCustomer }} - {{ props.cart?.customerFirstName }} {{ props.cart?.customerLastName }}
      </div>
      <div class="col-6 consultant-name-div">
        {{ t('partyOrderEntry.ConsultantName') }}: {{ props.cart?.beeNumberPayee }} - {{ props.cart?.payeeFirstName }} {{ props.cart?.payeeLastName }}
      </div>
    </div>
    <div v-if="!(props.cart?.isHostCart && props.cart?.isConsultantCart)" class="row mt-3 option-title-row">
      <div class="col-12 option-title-div">
        {{ t('partyOrderEntry.Options') }}:
      </div>
    </div>
    <!-- Reactivate Cart Checkbox - only visible for inactive carts -->
    <div v-if="!props.cart?.isHostCart && !props.cart?.isActive" class="row mt-3 reactivate-cart-row">
      <div class="col-12 reactivate-cart-div">
        <KCheckBox
          class="chbx-reactivate-cart"
          :checked="false"
          :label="t('partyOrderEntry.ReactivateCart')"
          @change="reactivateCartHandler($event)">
        </KCheckBox>
      </div>
    </div>
    <div v-if="!(props.cart?.isHostCart && props.cart?.isConsultantCart)" class="row mt-3 become-consultant-row">
      <div class="col-12 become-consultant-div">
        <KCheckBox
          class="chbx-become-consultant"
          :checked="interestedInBecomeConsultant != null ? interestedInBecomeConsultant : props.cart?.isConsultantLead"
          :label="t('partyOrderEntry.BecomeConsultant')"
          @change="changeConsultantLeadCheckboxHandler($event)">
        </KCheckBox>
      </div>
    </div>
    <div v-if="!(props.cart?.isHostCart && props.cart?.isConsultantCart)" class="row mt-3 host-party-row">
      <div :class="isPartyDateSaved ? 'col-md-6' : 'col-md-3'" class="col-12 host-party-div">
        <KCheckBox
          class="chbx-host-party"
          @change="hostPartyCheckboxClickedHandler($event)"
          :checked="interestedInHostParty != null ? interestedInHostParty : props.cart?.isPartyLead"
          :disabled="isPartyDateSaved"
          :label="isPartyDateSaved ? t('partyOrderEntry.HostingPartyOn', { date: hostPartyDate ? formatDate(hostPartyDate) : '' }) : t('partyOrderEntry.HostAParty')">
        </KCheckBox>
      </div>
      <div v-if="((interestedInHostParty  != null && interestedInHostParty) || props.cart?.isPartyLead) && !isCancelled && !isPartyDateSaved" class="col-12 col-md-4 party-date-label-div">
        <span class="party-date-label">{{ t('partyOrderEntry.PartyDate') }}:</span>
        <LocalizationProvider :language="getKendoLanguage(locale)">
          <IntlProvider :locale="getKendoLocale(locale)">
            <DatePicker
              :style="{ width: '230px' }"
              class="party-date-picker"
              :value="isCancelled ? undefined : hostPartyDate ?? undefined"
              @change="leadPartyDateChangedHandler($event)"
              :format="(dateFormat as any)"
              :formatPlaceholder="(formatPlaceholder as any)"
              :placeholder="t('partyOrderEntry.SelectDate')">
            </DatePicker>
          </IntlProvider>
        </LocalizationProvider>
      </div>
      <!-- Save button - shown when date is not saved -->
      <div v-if="((interestedInHostParty  != null && interestedInHostParty) || props.cart?.isPartyLead) && !isCancelled && !isPartyDateSaved"  class="col-6 col-md-2 party-date-save-div">
        <KButton
          @click="hostPartyDateClickedHandler($event)"
          class="baseBtn primaryBtn save-host-party-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'">
          {{ t('partyOrderEntry.SaveHostingEvent') }}
        </KButton>
      </div>
      <!-- Change Party Date button - shown when date is saved -->
      <div v-if="((interestedInHostParty  != null && interestedInHostParty) || props.cart?.isPartyLead) && !isCancelled && isPartyDateSaved"  class="col-12 col-md-3 mt-1 mb-1 m-0 party-date-change-div">
        <KButton
          @click="changePartyDateClickedHandler($event)"
          class="baseBtn primaryBtn change-party-date-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'">
          {{ t('partyOrderEntry.ChangePartyDate') }}
        </KButton>
      </div>
      <div v-if="((interestedInHostParty  != null && interestedInHostParty) || props.cart?.isPartyLead) && !isCancelled"  :class="isPartyDateSaved ? 'col-12 col-md-3' : 'col-6 col-md-2'" class="party-date-cancel-div">
        <KButton
          @click="hostPartyDateCancelClickedHandler($event)"
          class="baseBtn secondaryBtn cancel-host-party-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'">
          {{ t('partyOrderEntry.CancelHostingEvent') }}
        </KButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.reactivate-cart-div label,
.become-consultant-div label,
.host-party-div label
{
  @include typography-body;
}

.chbx-reactivate-cart input[type="checkbox"],
.chbx-become-consultant input[type="checkbox"],
.chbx-host-party input[type="checkbox"]
{
  margin-right: 10px;
}

.party-date-label
{
  margin-right: 10px;
}

@media (max-width: 768px) {
  .aoe-guest-header-control {
    .party-date-save-div {
      padding-right: $spacing-1;
    }

    .party-date-cancel-div {
      padding-left: $spacing-1;
    }
  }

  .party-date-picker {
    margin: $spacing-1 0;
  }

  .aoe-guest-header-control {
    .change-party-date-button, .cancel-host-party-button {
      float: right;
    }
  }
}


.k-calendar-title{
  padding: 0 0 0 1rem !important;
}
.k-datetime-calendar-wrap{
  width: calc(256px + var(--kendo-spacing-4, 1rem) * 2) !important;
}
</style>
